import { computed, onMounted, onUnmounted, ref } from 'vue';
import { trackAnalyticsLog } from '@/utils/analytics-track';

const SNOOZE_KEY = 'ds-pwa-install-snooze-date';
const DISMISS_SESSION_KEY = 'ds-pwa-install-dismissed';
/** 本会话已自动唤起过系统安装窗（避免反复弹） */
const AUTO_PROMPT_SESSION_KEY = 'ds-pwa-auto-prompted';
/** 用户已安装到桌面（appinstalled / standalone / relatedApps） */
const INSTALLED_KEY = 'ds-pwa-installed-v2';
const LEGACY_INSTALLED_KEY = 'ds-pwa-installed';

/** @type {import('vue').Ref<Event & { prompt: () => Promise<void>, userChoice: Promise<{ outcome: string }> } | null>} */
const deferredPrompt = ref(null);
const bannerOpen = ref(false);
const installed = ref(false);
/** 安装状态是否已探测完成（避免未安装探测前误弹） */
const installStateReady = ref(false);
/** 安全上下文下等了一阵仍无 beforeinstallprompt → 显示菜单安装指引 */
const androidMenuFallback = ref(false);
const installBusy = ref(false);
/** 0–100：安装流程可视化进度（系统 API 无真实进度，前端阶段模拟） */
const installProgress = ref(0);

let listenersBound = false;
/** 防止并发多次 auto prompt */
let autoPromptInFlight = false;
/** @type {ReturnType<typeof setInterval> | 0} */
let progressTimer = 0;
/** @type {Set<() => void>} */
const promptReadyListeners = new Set();

function clearInstallProgressTimer() {
  if (progressTimer && typeof window !== 'undefined') {
    window.clearInterval(progressTimer);
  }
  progressTimer = 0;
}

function startInstallProgress() {
  clearInstallProgressTimer();
  installProgress.value = 8;
  if (typeof window === 'undefined') return;
  progressTimer = window.setInterval(() => {
    const cur = installProgress.value;
    if (cur >= 88) return;
    const step = cur < 40 ? 7 : cur < 70 ? 4 : 2;
    installProgress.value = Math.min(88, cur + step);
  }, 220);
}

function finishInstallProgress(success) {
  clearInstallProgressTimer();
  installProgress.value = success ? 100 : 0;
}

function formatLocalDateKey(ms = Date.now()) {
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isSnoozedToday() {
  if (typeof localStorage === 'undefined') return false;
  const saved = String(localStorage.getItem(SNOOZE_KEY) ?? '').trim();
  if (!saved) return false;
  const today = formatLocalDateKey();
  if (saved === today) return true;
  localStorage.removeItem(SNOOZE_KEY);
  return false;
}

function snoozeToday() {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(SNOOZE_KEY, formatLocalDateKey());
}

function isDismissedThisSession() {
  if (typeof sessionStorage === 'undefined') return false;
  return sessionStorage.getItem(DISMISS_SESSION_KEY) === '1';
}

function markDismissedThisSession() {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(DISMISS_SESSION_KEY, '1');
}

function markAutoPromptedThisSession() {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(AUTO_PROMPT_SESSION_KEY, '1');
}

function readInstalledFlag() {
  if (typeof localStorage === 'undefined') return false;
  // v1 会在 userChoice=accepted 时误写“已安装”；升级后不再信任该旧标记。
  localStorage.removeItem(LEGACY_INSTALLED_KEY);
  return localStorage.getItem(INSTALLED_KEY) === '1';
}

function markInstalledPersisted() {
  installed.value = true;
  bannerOpen.value = false;
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(INSTALLED_KEY, '1');
}

function clearInstalledPersisted() {
  installed.value = false;
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(INSTALLED_KEY);
  localStorage.removeItem(LEGACY_INSTALLED_KEY);
}

export function isStandaloneDisplay() {
  if (typeof window === 'undefined') return false;
  if (window.__DS_IS_PWA_STANDALONE__) return true;
  try {
    if (window.matchMedia?.('(display-mode: standalone)')?.matches) return true;
  } catch {
    /* ignore */
  }
  return Boolean(window.navigator?.standalone);
}

/** 真正以已安装 PWA 窗口运行（不含浏览器里手动带 ?source=pwa） */
export function isRunningAsInstalledPwa() {
  if (typeof window === 'undefined') return false;
  try {
    if (window.matchMedia?.('(display-mode: standalone)')?.matches) return true;
  } catch {
    /* ignore */
  }
  return Boolean(window.navigator?.standalone);
}

export function isIosSafari() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const webkit = /WebKit/i.test(ua);
  const isChromeIos = /CriOS/i.test(ua);
  const isFirefoxIos = /FxiOS/i.test(ua);
  return iOS && webkit && !isChromeIos && !isFirefoxIos;
}

/** 任意 iOS 浏览器（Safari / Chrome / 等）— 均无 beforeinstallprompt */
export function isIosDevice() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

/** 任意 Android 设备（不限浏览器内核） */
export function isAndroidDevice() {
  if (typeof navigator === 'undefined') return false;
  return /Android/i.test(navigator.userAgent || '');
}

/** Chromium 系安卓（含 Chrome / Edge / Samsung Internet） */
export function isAndroidChromium() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (!/Android/i.test(ua)) return false;
  // Edge 须单独匹配 EdgA / Edg/（UA 里也有 Chrome）
  return /EdgA|Edg\/|SamsungBrowser|Chrome|CriOS/i.test(ua) && !/Firefox|FxiOS/i.test(ua);
}

/** 局域网 IP 主机：自签 https 可打开页面，但 Chrome 不会发 beforeinstallprompt */
function isLanIpHostname() {
  if (typeof window === 'undefined') return false;
  const host = String(window.location?.hostname || '');
  return /^\d{1,3}(?:\.\d{1,3}){3}$/.test(host);
}

export function isSecureInstallContext() {
  if (typeof window === 'undefined') return false;
  return Boolean(window.isSecureContext);
}

function notifyPromptReadyListeners() {
  promptReadyListeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ignore */
    }
  });
}

function capturePromptEvent(event) {
  if (!event || typeof event.prompt !== 'function') return;
  try {
    event.preventDefault();
  } catch {
    /* already prevented in index.html */
  }
  // 又能弹出系统安装窗 → 说明已卸载或未装，清掉「已安装」标记
  clearInstalledPersisted();
  deferredPrompt.value = event;
  androidMenuFallback.value = false;
  if (typeof window !== 'undefined' && window.__DS_PWA__) {
    window.__DS_PWA__.deferredPrompt = event;
  }
  notifyPromptReadyListeners();
}

function hydratePromptFromWindow() {
  if (typeof window === 'undefined') return;
  const early = window.__DS_PWA__?.deferredPrompt;
  if (early && typeof early.prompt === 'function') {
    clearInstalledPersisted();
    deferredPrompt.value = early;
    androidMenuFallback.value = false;
  }
}

function onBeforeInstallPrompt(event) {
  capturePromptEvent(event);
}

function onAppInstalled() {
  markInstalledPersisted();
  deferredPrompt.value = null;
  androidMenuFallback.value = false;
  if (typeof window !== 'undefined' && window.__DS_PWA__) {
    window.__DS_PWA__.deferredPrompt = null;
  }
  markDismissedThisSession();
  markAutoPromptedThisSession();
}

/**
 * 探测是否已安装桌面快捷方式 / PWA。
 * - 独立窗口 / iOS standalone
 * - localStorage（appinstalled 写入）
 * - getInstalledRelatedApps（需 manifest.related_applications）
 */
async function detectPwaInstalled() {
  if (isRunningAsInstalledPwa()) return true;
  if (deferredPrompt.value) return false;

  if (readInstalledFlag()) return true;

  if (typeof navigator !== 'undefined' && typeof navigator.getInstalledRelatedApps === 'function') {
    try {
      const apps = await navigator.getInstalledRelatedApps();
      if (Array.isArray(apps) && apps.length > 0) return true;
    } catch {
      /* ignore */
    }
  }
  return false;
}

/**
 * 尽早挂载监听：async 横幅组件挂载前 beforeinstallprompt 可能已触发一次。
 * 在 main.js 调用。
 */
export function ensurePwaInstallListeners() {
  if (typeof window === 'undefined' || listenersBound) return;
  listenersBound = true;
  hydratePromptFromWindow();
  window.__DS_PWA_ON_PROMPT__ = capturePromptEvent;
  window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  window.addEventListener('appinstalled', onAppInstalled);
}

function wait(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

export async function waitForDeferredPrompt(timeoutMs = 2500) {
  if (deferredPrompt.value) return deferredPrompt.value;
  hydratePromptFromWindow();
  if (deferredPrompt.value) return deferredPrompt.value;

  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    await wait(200);
    hydratePromptFromWindow();
    if (deferredPrompt.value) return deferredPrompt.value;
  }
  return null;
}

function clearDeferredPrompt() {
  deferredPrompt.value = null;
  if (typeof window !== 'undefined' && window.__DS_PWA__) {
    window.__DS_PWA__.deferredPrompt = null;
  }
}

/**
 * Android / Chrome：beforeinstallprompt → 快捷添加到桌面
 * iOS Safari：图文引导「分享 → 添加到主屏幕」
 */
export function usePwaInstall() {
  ensurePwaInstallListeners();

  async function checkInstalled() {
    hydratePromptFromWindow();
    const already = await detectPwaInstalled();
    if (already) markInstalledPersisted();
    return already;
  }

  const canNativeInstall = computed(() => Boolean(deferredPrompt.value));
  const showIosGuide = computed(
    () =>
      installStateReady.value &&
      !installed.value &&
      isIosDevice() &&
      !isRunningAsInstalledPwa() &&
      bannerOpen.value
  );
  /** 安卓：仅未安装且可展示时弹出 */
  const showAndroidQuickAdd = computed(
    () =>
      installStateReady.value &&
      !installed.value &&
      bannerOpen.value &&
      !isRunningAsInstalledPwa() &&
      !isIosDevice() &&
      isAndroidChromium() &&
      isSecureInstallContext()
  );
  const showAndroidInstall = showAndroidQuickAdd;
  const showAndroidInsecureHint = computed(
    () =>
      installStateReady.value &&
      !installed.value &&
      bannerOpen.value &&
      !isRunningAsInstalledPwa() &&
      !canNativeInstall.value &&
      !isIosDevice() &&
      isAndroidChromium() &&
      !isSecureInstallContext()
  );
  const showAndroidMenuHint = computed(
    () =>
      showAndroidQuickAdd.value &&
      !canNativeInstall.value &&
      androidMenuFallback.value &&
      !isLanIpHostname()
  );
  const showAndroidLocalIpHint = computed(
    () =>
      showAndroidQuickAdd.value &&
      !canNativeInstall.value &&
      androidMenuFallback.value &&
      isLanIpHostname()
  );
  const visible = computed(
    () => showIosGuide.value || showAndroidQuickAdd.value || showAndroidInsecureHint.value
  );

  function maybeOpenBanner() {
    if (!installStateReady.value) return;
    if (installed.value || isRunningAsInstalledPwa()) {
      bannerOpen.value = false;
      return;
    }
    if (isSnoozedToday() || isDismissedThisSession()) {
      bannerOpen.value = false;
      return;
    }
    // 安卓：只有拿到系统安装事件才弹（已安装时 Chrome 不会发事件）
    if (isAndroidChromium() && isSecureInstallContext()) {
      if (deferredPrompt.value) {
        if (!bannerOpen.value) {
          bannerOpen.value = true;
          trackAnalyticsLog({
            key: 'pwa.banner_show',
            label: 'PWA安装横幅展示',
            value: { platform: 'android' },
          });
        }
      }
      return;
    }
    if (isIosDevice() || (isAndroidChromium() && !isSecureInstallContext())) {
      if (!bannerOpen.value) {
        bannerOpen.value = true;
        trackAnalyticsLog({
          key: 'pwa.banner_show',
          label: 'PWA安装横幅展示',
          value: { platform: isIosDevice() ? 'ios' : 'android_insecure' },
        });
      }
    }
  }

  /** iOS：用户确认已添加到主屏幕 → 之后不再弹 */
  function acknowledgeIosInstalled() {
    markInstalledPersisted();
    markDismissedThisSession();
    bannerOpen.value = false;
  }

  /** 快捷添加：只消费已经捕获的安装事件，确保 prompt() 仍在用户手势链路中。 */
  async function promptInstall() {
    if (installed.value || isRunningAsInstalledPwa()) {
      bannerOpen.value = false;
      return { outcome: 'installed' };
    }
    trackAnalyticsLog({
      key: 'pwa.install.click',
      label: 'PWA点击安装',
      value: null,
    });
    installBusy.value = true;
    startInstallProgress();
    bannerOpen.value = true;
    try {
      const promptEvent = deferredPrompt.value;
      if (!promptEvent) {
        // 点击后不再等待事件：没有原生安装能力就立即进入菜单/换浏览器引导。
        finishInstallProgress(false);
        const already = await detectPwaInstalled();
        if (already) {
          markInstalledPersisted();
          bannerOpen.value = false;
          trackAnalyticsLog({
            key: 'pwa.install.result',
            label: 'PWA安装结果',
            value: { outcome: 'installed' },
          });
          return { outcome: 'installed' };
        }
        androidMenuFallback.value = true;
        bannerOpen.value = true;
        trackAnalyticsLog({
          key: 'pwa.install.result',
          label: 'PWA安装结果',
          value: { outcome: 'unavailable' },
        });
        return { outcome: 'unavailable' };
      }
      markAutoPromptedThisSession();
      installProgress.value = Math.max(installProgress.value, 55);
      promptEvent.prompt();
      const choice = await promptEvent.userChoice;
      clearDeferredPrompt();
      if (choice?.outcome === 'accepted') {
        // accepted ≠ 桌面已有图标；不展示「安装完成」，真正完成以 appinstalled / standalone 为准。
        finishInstallProgress(false);
        bannerOpen.value = false;
        markDismissedThisSession();
      } else {
        finishInstallProgress(false);
        androidMenuFallback.value = true;
        maybeOpenBanner();
      }
      trackAnalyticsLog({
        key: 'pwa.install.result',
        label: 'PWA安装结果',
        value: { outcome: choice?.outcome || 'dismissed' },
      });
      return choice || { outcome: 'dismissed' };
    } catch (err) {
      finishInstallProgress(false);
      throw err;
    } finally {
      installBusy.value = false;
      clearInstallProgressTimer();
      if (installProgress.value < 100) installProgress.value = 0;
    }
  }

  /**
   * 安卓：等 beforeinstallprompt；有则弹安装提示，无则视为已安装/不可装（不弹）。
   */
  async function tryAutoNativeInstall() {
    if (autoPromptInFlight) return;
    if (installed.value || isRunningAsInstalledPwa()) return;
    if (!isAndroidChromium() || !isSecureInstallContext()) return;
    if (isSnoozedToday() || isDismissedThisSession()) return;

    autoPromptInFlight = true;
    try {
      if (!deferredPrompt.value) {
        await waitForDeferredPrompt(2800);
      }
      if (
        !deferredPrompt.value &&
        typeof navigator !== 'undefined' &&
        navigator.serviceWorker?.ready
      ) {
        try {
          await Promise.race([navigator.serviceWorker.ready, wait(2000)]);
        } catch {
          /* ignore */
        }
        await waitForDeferredPrompt(1200);
      }
      if (deferredPrompt.value) {
        maybeOpenBanner();
        return;
      }
      // 无事件：再确认一次 relatedApps / 本地标记
      const already = await detectPwaInstalled();
      if (already) {
        markInstalledPersisted();
      }
      bannerOpen.value = false;
    } finally {
      autoPromptInFlight = false;
    }
  }

  function dismiss({ snoozeTodayFlag = false } = {}) {
    clearInstallProgressTimer();
    installBusy.value = false;
    installProgress.value = 0;
    if (bannerOpen.value) {
      trackAnalyticsLog({
        key: 'pwa.banner_dismiss',
        label: '关闭PWA安装横幅',
        value: { snoozeToday: Boolean(snoozeTodayFlag) },
      });
    }
    bannerOpen.value = false;
    markDismissedThisSession();
    if (snoozeTodayFlag) snoozeToday();
  }

  let autoTimer = 0;
  /** @type {(() => void) | null} */
  let onPromptReady = null;

  onMounted(() => {
    if (typeof window === 'undefined') return;

    (async () => {
      hydratePromptFromWindow();
      const already = await detectPwaInstalled();
      if (already) {
        markInstalledPersisted();
        installStateReady.value = true;
        bannerOpen.value = false;
        return;
      }
      installed.value = false;
      installStateReady.value = true;

      onPromptReady = () => {
        maybeOpenBanner();
      };
      promptReadyListeners.add(onPromptReady);

      if (isIosDevice() || (isAndroidChromium() && !isSecureInstallContext())) {
        window.setTimeout(() => maybeOpenBanner(), 1200);
      }

      if (isAndroidChromium() && isSecureInstallContext()) {
        // 已有事件则立刻弹；否则短等 BIP
        if (deferredPrompt.value) {
          maybeOpenBanner();
        } else {
          autoTimer = window.setTimeout(() => {
            tryAutoNativeInstall();
          }, 600);
        }
      }
    })();
  });

  onUnmounted(() => {
    if (onPromptReady) promptReadyListeners.delete(onPromptReady);
    clearInstallProgressTimer();
    if (typeof window !== 'undefined' && autoTimer) {
      window.clearTimeout(autoTimer);
    }
  });

  return {
    visible,
    showIosGuide,
    showAndroidInstall,
    showAndroidQuickAdd,
    showAndroidInsecureHint,
    showAndroidMenuHint,
    showAndroidLocalIpHint,
    canNativeInstall,
    installBusy,
    installProgress,
    installed,
    checkInstalled,
    promptInstall,
    acknowledgeIosInstalled,
    dismiss,
  };
}
